<template>
  <div>
    <div id="gmap-altimeter__container" v-show="showAltimeter">
      <canvas id="gmap-altimeter" height="300" width="100"></canvas>
    </div>
    <GmapMap
      ref="mapRef"
      :center="center"
      :zoom="zoomSize(center)"
      :options="{
        mapTypeControl: true,
        streetViewControl: true,
        zoomControl: true,
        fullscreenControl: true,
        disableDefaultUi: false,
        rotateControl: true,
      }"
      style="width: 100%; height: 100%"
    >
      <GmapCircle
        :center="center"
        :radius="center.latlng_accuracy"
        :visible="true"
        :options="{fillColor: 'red', fillOpacity: 0.15, strokeWeight: 2, strokeColor: '#f38038', strokeOpacity: 0.4}"
      ></GmapCircle>
      <GmapCustomMarker
        v-for="(m, index) in markers"
        :key="index"
        :marker="m.position"
      >
        <div
          :class="{
            marker: true,
            current: isCurrentPosition(m)
          }"
        >
          <div
            class="marker__circle"
            v-tippy="{
              arrow: true,
              trigger: 'mouseenter focus',
              hideOnClick: false,
              theme: 'light',
              content: `
              <div style='text-align: left'>
                <div>${m.number}</div>
                <div>${getDate(m.ts)} at ${getTime(m.ts)}</div>
              </div>`,
            }"
            :style="{ backgroundColor: m.color }"
          ></div>
          <div class="marker__pin"></div>
        </div>
      </GmapCustomMarker>
    </GmapMap>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapMutations } from "vuex";
import GmapCustomMarker from "vue2-gmap-custom-marker";
import { isEqual } from "lodash-es";

export default {
  name: "GoogleMap",
  props: {
    geography: {
      type: Array, 
      default: () => []
    },
    recipientId: {
      type: String,
    },
  },
  components: {
    GmapCustomMarker,
  },
  data() {
    return {
      altimeterContainer: null,
      center: {
        lat: 41,
        lng: -71
      },
      markers: [],
      markerKeys: [],
      recipients: [],
      tooltips: [],
      latestPosition: {},
    };
  },
  methods: {
    ...mapMutations({
      updateLastHat: "conversations/updateLastHat",
    }),
    zoomSize(pos) {
      if (pos.latlng_accuracy < 20)
        return 18;
      else if (pos.latlng_accuracy < 50)
        return 17;
      else if (pos.latlng_accuracy < 100)
        return 16;
      else if (pos.latlng_accuracy < 800)
        return 15;
      else if (pos.latlng_accuracy < 2000)
        return 14;
      else if (pos.latlng_accuracy < 5000)
        return 12;
      else if (pos.latlng_accuracy < 10000)
        return 10;
      else
        return 10;
    },
    addMarker(geoLat, geoLng, ts, recipientId) {
      const marker = {
        lat: geoLat,
        lng: geoLng,
      };
      const conversation = this.activeConversations.find(convo => convo.conversation_id == this.currentConversation.conversation_id);
      const recipients = conversation ? conversation.recipients : [];
      const recipient = recipients.find(i => i.recipient_id == recipientId);
      let color = "green";
      if (recipient) {
        if (recipient.recipient_id == this.recipientId) {
          color = "green";
        } else {
          const prevMarker = this.markers.find(m => m.number == recipient.number);
          if (prevMarker) {
            color = prevMarker.color;
          } else {
            const usedColors = this.markers.map(m => m.color);
            const colorNotUsed = this.colors.find(c => !usedColors.includes(c));
            color = colorNotUsed;
          }
        }
      }
      let number = recipient ? recipient.number : "";
      this.markerKeys.push(geoLat + "," + geoLng + "," + recipientId);
      this.markers.push({
        position: marker,
        ts,
        number,
        color,
        recipient_id: recipientId,
      });
    },
    getDate(ts) {
      return new Date(ts).toString().substring(4, 15);
    },
    getTime(ts) {
      return new Date(ts).toString().substring(16, 24);
    },
    isCurrentPosition(marker) {
      const { position, recipient_id } = marker;

      if (recipient_id != this.recipientId) {
        return false;
      }

      return (
        position.lat === this.latestPosition.lat &&
        position.lng === this.latestPosition.lng
      );
    },
    setAltimeter() {
      this.altimeter.addDevice({
        id: "Recipient",
        icon: require("../../src/assets/images/recipient.png"),
      });
    },
    showAltimeterElement() {
      if (this.altimeterContainer) {
        this.altimeterContainer.style.display = this.showAltimeter
          ? "block"
          : "none";
      }
    },
    updateAltimeter() {
      if (this.altimeter) {
        this.altimeter.updateDevice({
          id: "Recipient",
          latitude: this.latestPosition.lat,
          longitude: this.latestPosition.lng,
          hat: this.latestPosition.hat || false,
          hae: this.latestPosition.hae || false,
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      activeConversations: "conversations/activeConversations",
      currentConversation: "conversations/currentConversation",
      recipientLastHat: "conversations/recipientLastHat",
      colors: "user/colors",
    }),
    showAltimeter() {
      return !!this.recipientLastHat;
    },
  },
  watch: {
    geography: {
      handler(geography) {
        if (Array.isArray(geography) && geography.length > 0) {
          geography.forEach((geog) => {
            if (!this.markerKeys.includes(geog.lat + "," + geog.lng + "," + geog.recipient_id))
              this.addMarker(geog.lat, geog.lng, geog.ts || geog.updated_at, geog.recipient_id);
          });

          const lastGeoFromPrimaryRecipient = geography.find(geo => geo.recipient_id == this.recipientId);
          // Prevent re-render if recipient is not moving.
          if (lastGeoFromPrimaryRecipient) {
            if (!isEqual(lastGeoFromPrimaryRecipient, this.center)) {
              this.center = {...lastGeoFromPrimaryRecipient};
              this.latestPosition = {...lastGeoFromPrimaryRecipient};
              this.updateAltimeter();
            }

            if (lastGeoFromPrimaryRecipient.hat) {
              this.updateLastHat(lastGeoFromPrimaryRecipient.hat);
            }
          }

          this.showAltimeterElement();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    const alt = require("altimeter");

    /* Initialize altimeter */
    this.altimeter = new alt.AltimeterChart(
      document.getElementById("gmap-altimeter")
    );

    this.altimeter.addDevice({
      id: "Recipient",
      icon: require("../../src/assets/images/recipient.png"),
    });

    /* Wait for google maps to be fully functional */
    this.$refs.mapRef.$mapPromise.then(() => {
      setTimeout(() => {
        /* Google map  style element */
        const mapContainer = this.$el.querySelector(".gm-style");

        this.altimeterContainer = this.$el.querySelector(
          "#gmap-altimeter__container"
        );
        this.showAltimeterElement();

        mapContainer.appendChild(this.altimeterContainer);

        const lastGeoFromPrimaryRecipient = this.geography.find(geo => geo.recipient_id == this.recipientId);
        if (lastGeoFromPrimaryRecipient) {
          this.center = {...lastGeoFromPrimaryRecipient};
          this.latestPosition = {...lastGeoFromPrimaryRecipient};
          this.updateAltimeter();
        }
      }, 5000);
    });
  },
};
</script>

<style lang="scss" scoped>
.marker {
  display: relative;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 30px;
  width: 25px;

  z-index: 998;

  &__circle {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -100%);
    height: 15px;
    width: 15px;
    border-radius: 100%;
    border: 2px solid white;
    box-shadow: 0 0 0 2px hsl(0, 0%, 80%), 0 0 0 3px hsl(0, 0%, 90%);

    z-index: 999;
  }

  &__pin {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 10%;
    height: 70%;

    background-color: white;
    box-shadow: 0 0 0 2px hsl(0, 0%, 80%), 0 0 0 3px hsl(0, 0%, 90%);
    border-bottom-left-radius: 25px;
    border-bottom-right-radius: 25px;
  }

  &.current &__circle {
    height: 25px;
    width: 25px;
  }

  &.current &__pin {
    width: 15%;
  }
}

#gmap-altimeter__container {
  display: none;
  position: absolute;
  left: 10px;
  top: 50px;
  width: 70px;
  min-height: 250px;
  height: 250px;
  z-index: 9999;
}

#gmap-altimeter {
  height: 250px !important;
}
</style>
